/**
 * SecureEmbed DataStores Page
 *
 * Energy portal landing page — datasource overview cards grouped by category
 * with filter selector, [i] tooltips, browse links, and full description wrap.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock, RefreshCw, HardDrive, FolderOpen,
  Loader2, AlertCircle, Activity, Target, Database, Building2,
  BookOpen, FileText, Zap, Globe, Network, MapPin, DollarSign,
  Construction, MessageSquare, ClipboardCheck, Search, Filter
} from 'lucide-react';
import { metadataApi, partnerApi } from '../services/api';
import { DATASOURCE_COLORS } from '../constants/colors';
import InfoTooltip from '../components/InfoTooltip';
import RelatedPagesCard from '../components/RelatedPagesCard';

const GROUPS = {
  'energy-markets': { label: 'Energy Markets', order: 0 },
  'weather': { label: 'Weather & Environment', order: 1 },
  'regulatory': { label: 'Regulatory & Compliance', order: 2 },
  'research': { label: 'Research & Knowledge', order: 3 },
  'grid': { label: 'Grid Infrastructure', order: 4 },
  'pipeline': { label: 'Development Pipeline', order: 5 },
  'organisations': { label: 'Organisations & Partners', order: 6 },
} as const;

type GroupKey = keyof typeof GROUPS;

const DATASOURCE_CARDS: { key: string; label: string; icon: React.ElementType; group: GroupKey }[] = [
  { key: 'aemo', label: 'AEMO Energy', icon: Activity, group: 'energy-markets' },
  { key: 'aemo-duid', label: 'AEMO DUID', icon: Zap, group: 'energy-markets' },
  { key: 'aemo-fcas', label: 'AEMO FCAS', icon: Zap, group: 'energy-markets' },
  { key: 'bom', label: 'BOM Weather', icon: Globe, group: 'weather' },
  { key: 'aer', label: 'AER Regulatory', icon: Target, group: 'regulatory' },
  { key: 'regulatory', label: 'Regulatory Docs', icon: FileText, group: 'regulatory' },
  { key: 'scholarly', label: 'Scholarly Research', icon: BookOpen, group: 'research' },
  { key: 'energy-organisations', label: 'Energy Organisations', icon: Building2, group: 'organisations' },
  { key: 'grid-connection-queue', label: 'Grid Connection Queue', icon: Network, group: 'grid' },
  { key: 'rez-capacity', label: 'REZ Capacity', icon: MapPin, group: 'grid' },
  { key: 'ppa-intelligence', label: 'PPA Intelligence', icon: DollarSign, group: 'pipeline' },
  { key: 'network-constraints', label: 'Network Constraints', icon: Network, group: 'grid' },
  { key: 'planning-approvals', label: 'Planning Approvals', icon: ClipboardCheck, group: 'pipeline' },
  { key: 'construction-milestones', label: 'Construction Milestones', icon: Construction, group: 'pipeline' },
  { key: 'community-sentiment', label: 'Community Sentiment', icon: MessageSquare, group: 'pipeline' },
  { key: 'partner', label: 'Partner & Quantum Discovery', icon: Building2, group: 'organisations' },
];

const Analytics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [dsStats, setDsStats] = useState<Record<string, number>>({});
  const [dsDetails, setDsDetails] = useState<Record<string, { records: number; dataSize: string; rawFiles: number; rawSize: string; timeSpan: string; description: string }>>({});
  const [partnerOrgCount, setPartnerOrgCount] = useState<number>(0);
  const [selectedGroup, setSelectedGroup] = useState<GroupKey | 'all'>('all');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsResult, partnerResult] = await Promise.allSettled([
        metadataApi.getStats(),
        partnerApi.getStats(),
      ]);

      if (statsResult.status === 'fulfilled' && statsResult.value?.success) {
        setDsStats(statsResult.value.data?.record_counts || {});
        setDsDetails(statsResult.value.data?.datasource_details || {});
        setLastUpdated(new Date());
      }

      if (partnerResult.status === 'fulfilled' && partnerResult.value?.success) {
        setPartnerOrgCount(partnerResult.value.data?.total_organisations || 0);
      }

      if (statsResult.status === 'rejected' && partnerResult.status === 'rejected') {
        setError('Failed to load analytics. Please ensure the environment is deployed.');
      }
    } catch (err: any) {
      console.error('Analytics error:', err);
      setError(err.message || 'Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const filteredCards = useMemo(() => {
    if (selectedGroup === 'all') return DATASOURCE_CARDS;
    return DATASOURCE_CARDS.filter(c => c.group === selectedGroup);
  }, [selectedGroup]);

  const groupedCards = useMemo(() => {
    const groups: { key: GroupKey; label: string; cards: typeof DATASOURCE_CARDS }[] = [];
    const sortedGroupKeys = (Object.keys(GROUPS) as GroupKey[]).sort(
      (a, b) => GROUPS[a].order - GROUPS[b].order
    );
    for (const gk of sortedGroupKeys) {
      const cards = filteredCards.filter(c => c.group === gk);
      if (cards.length > 0) {
        groups.push({ key: gk, label: GROUPS[gk].label, cards });
      }
    }
    return groups;
  }, [filteredCards]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DataStores</h1>
          <p className="text-gray-500 mt-1">Data stores and platform overview</p>
        </div>
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {lastUpdated && !loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-800">Data Unavailable</h4>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Group Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-gray-400" />
        <button
          onClick={() => setSelectedGroup('all')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
            selectedGroup === 'all'
              ? 'bg-[#2B064F] text-white border-[#2B064F]'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
          }`}
        >
          All Groups
        </button>
        {(Object.keys(GROUPS) as GroupKey[])
          .sort((a, b) => GROUPS[a].order - GROUPS[b].order)
          .map(gk => (
            <button
              key={gk}
              onClick={() => setSelectedGroup(gk)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                selectedGroup === gk
                  ? 'bg-[#2B064F] text-white border-[#2B064F]'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {GROUPS[gk].label}
            </button>
          ))}
      </div>

      {/* Grouped Datasource Cards */}
      {groupedCards.map(group => (
        <div key={group.key}>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">{group.label}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {group.cards.map(item => {
              const ds = DATASOURCE_COLORS[item.key] || DATASOURCE_COLORS.custom;
              const count = item.key === 'partner' ? (partnerOrgCount || dsStats[item.key]) : dsStats[item.key];
              const detail = dsDetails[item.key];
              return (
                <div key={item.key} className="bg-white rounded-xl p-5 border-2 shadow-sm" style={{ borderColor: ds.hex + '40' }}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: ds.hex + '20' }}>
                      <item.icon className="w-5 h-5" style={{ color: ds.hex }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-1">
                        {item.label}
                        <InfoTooltip tooltipId={`analytics.ds.${item.key}`} />
                      </h3>
                      {detail && (
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{detail.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 mb-3">
                    {count ? formatNumber(count) : '--'}
                    <span className="text-sm font-normal text-gray-500 ml-1.5">records</span>
                  </div>
                  {detail && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <HardDrive className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-500">Processed:</span>
                        <span className="font-medium">{detail.dataSize}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <FolderOpen className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-500">Raw:</span>
                        <span className="font-medium">{detail.rawFiles.toLocaleString()} files</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Database className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-500">Raw size:</span>
                        <span className="font-medium">{detail.rawSize}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-500">Span:</span>
                        <span className="font-medium">{detail.timeSpan}</span>
                      </div>
                    </div>
                  )}
                  {count && count > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                      <Link
                        to={`/datastores/${item.key}/browse`}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-xs"
                      >
                        <Search className="w-3 h-3" />
                        Browse
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {loading && (
        <div className="fixed inset-0 bg-white/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg flex items-center gap-4">
            <Loader2 className="w-6 h-6 text-[#2B064F] animate-spin" />
            <span className="text-gray-700">Loading analytics...</span>
          </div>
        </div>
      )}

      <RelatedPagesCard currentRoute="/" />
    </div>
  );
};

export default Analytics;
